// Push Notification utilities

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Check if push notifications are fully supported (for actual subscription)
export const isPushSupported = () => {
  // Basic check for service worker and PushManager
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }
  
  // Check if Notification API is available
  if (!('Notification' in window)) {
    return false;
  }
  
  return true;
};

// Check if we should show the push notification prompt
// This is more lenient - we show the prompt if Notification API exists
export const shouldShowPushPrompt = () => {
  return 'Notification' in window;
};

// Check if this is iOS (iPhone or iPad)
export const isIOS = () => {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Check if this is iOS Safari (not Chrome or other browsers on iOS)
export const isIOSSafari = () => {
  if (!isIOS()) return false;
  const ua = window.navigator.userAgent;
  // Check it's not Chrome, Firefox, or other browsers on iOS
  const isChrome = /CriOS/.test(ua);
  const isFirefox = /FxiOS/.test(ua);
  const isOpera = /OPiOS/.test(ua);
  const isEdge = /EdgiOS/.test(ua);
  return !isChrome && !isFirefox && !isOpera && !isEdge;
};

// Check if running as standalone PWA (Home Screen app)
export const isStandalonePWA = () => {
  // Check for iOS standalone mode
  if (window.navigator.standalone === true) return true;
  // Check for other PWA display modes
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  return false;
};

// Key for session storage to track if overlay was shown
const PUSH_OVERLAY_SHOWN_KEY = 'push_overlay_shown';

// Register service worker
export const registerServiceWorker = async () => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Mark that we've shown the push overlay in this session
export const markPushOverlayShown = (eventSlug) => {
  try {
    sessionStorage.setItem(`${PUSH_OVERLAY_SHOWN_KEY}_${eventSlug}`, 'true');
  } catch (e) {
    // sessionStorage might not be available
  }
};

// Check if we've already shown the push overlay in this session
export const wasPushOverlayShown = (eventSlug) => {
  try {
    return sessionStorage.getItem(`${PUSH_OVERLAY_SHOWN_KEY}_${eventSlug}`) === 'true';
  } catch (e) {
    return false;
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isPushSupported()) {
    return 'unsupported';
  }
  
  const permission = await Notification.requestPermission();
  return permission;
};

// Subscribe to push notifications for an event
export const subscribeToPush = async (eventId, vapidPublicKey) => {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  const registration = await navigator.serviceWorker.ready;
  
  // Convert VAPID key to Uint8Array
  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
  
  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });
    
    // Send subscription to backend
    const response = await fetch(`${API}/events/${eventId}/push-subscriptions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64(subscription.getKey('auth'))
        }
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Subscription API error:', response.status, errorText);
      throw new Error(`Failed to save subscription: ${response.status} - ${errorText}`);
    }
    
    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push:', error);
    throw error;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async (eventId) => {
  if (!isPushSupported()) {
    return;
  }
  
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    await subscription.unsubscribe();
    
    // Remove from backend
    await fetch(`${API}/events/${eventId}/push-subscriptions?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
      method: 'DELETE'
    });
  }
};

// Check if already subscribed to push for an event
export const isPushSubscribed = async () => {
  if (!isPushSupported()) {
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!isPushSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Helper: Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Helper: Convert ArrayBuffer to base64
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
